// src/components/NewInvoice/InvoiceStonePopUp.jsx
import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { FaPlus, FaTrash, FaTimes } from "react-icons/fa";
import { useSelector } from "react-redux";
import {
  DECIMAL_RE,
  defaultStoneFields,
  handleDecimalKeyDown,
  handleDecimalPaste,
  handleIntegerBeforeInput,
  handleIntegerKeyDown,
  handleIntegerPaste,
} from "../../config/fieldsHelper";

const InvoiceStonePopUp = ({
  initialStones = [],
  setOpenStoneModal,
  onSave,
  onClose,
}) => {
  const stonesData = useSelector((s) => s.stones?.stones);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    setFocus,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      stones: initialStones?.length ? initialStones : [defaultStoneFields],
    },
    shouldUnregister: false,
  });

  const { fields, append, remove } = useFieldArray({ control, name: "stones" });

  // refs
  const rowRefs = useRef([]);
  const firstSelectRef = useRef(null);
  const panelRef = useRef(null);
  const prevInitRef = useRef(null);

  // 🔒 Focus control: only once per open/reset
  const focusArmedRef = useRef(true); // armed on mount/reset; disarm on user interaction or success

  const tryFocusRHF = () => {
    try {
      setFocus("stones.0.stnPopupName");
      return true;
    } catch {
      return false;
    }
  };

  const tryFocusDOM = () => {
    const el =
      firstSelectRef.current ||
      document.querySelector('select[name="stones.0.stnPopupName"]');
    if (!el) return false;
    if (typeof el.focus === "function") {
      el.focus({ preventScroll: true });
      rowRefs.current?.[0]?.scrollIntoView({ behavior: "instant", block: "center" });
      return document.activeElement === el;
    }
    return false;
  };

  const focusFirst = () => {
    if (!focusArmedRef.current) return true; // treat as done
    const a = tryFocusRHF();
    const b = tryFocusDOM();
    const ok = a || b;
    if (ok) focusArmedRef.current = false; // disarm after success
    return ok;
  };

  const focusWithRetries = (tries = 10, delay = 40) => {
    if (!focusArmedRef.current) return;
    let count = 0;
    const tick = () => {
      if (!focusArmedRef.current) return; // user interacted or success
      if (focusFirst() || count >= tries) return;
      count++;
      setTimeout(tick, delay);
    };
    // initial attempt + short retries (no long loops)
    if (!focusFirst()) setTimeout(tick, delay);
  };

  // Smooth scroll preference while modal is open (optional)
  useEffect(() => {
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = prev || "";
    };
  }, []);

  // Reset when initialStones changes, then focus (re-arm focus)
  useEffect(() => {
    const curr = JSON.stringify(initialStones || []);
    if (curr !== prevInitRef.current) {
      reset({
        stones: initialStones?.length ? initialStones : [defaultStoneFields],
      });
      focusArmedRef.current = true; // re-arm
      setTimeout(() => focusWithRetries(), 0);
      prevInitRef.current = curr;
    }
  }, [initialStones, reset]);

  // On first mount, arm and try focusing
  useEffect(() => {
    focusArmedRef.current = true;
    focusWithRetries();
  }, []);

  // (Optional) simple focus trap inside panel
  useEffect(() => {
    const root = panelRef.current;
    if (!root) return;

    const getFocusable = () =>
      Array.from(
        root.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled"));

    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const f = getFocusable();
      if (!f.length) return;
      const first = f[0],
        last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    // user focused anything inside → disarm any future auto focus
    const onFocusCapture = () => {
      focusArmedRef.current = false;
    };

    root.addEventListener("keydown", onKeyDown);
    root.addEventListener("focusin", onFocusCapture);
    return () => {
      root.removeEventListener("keydown", onKeyDown);
      root.removeEventListener("focusin", onFocusCapture);
    };
  }, []);

  const onNamePicked = (idx, id) => {
    const sel = (stonesData || []).find((s) => String(s?._id) === String(id));
    if (!sel) return;
    setValue(`stones.${idx}.unit`, sel?.unit ?? "");
    setValue(`stones.${idx}.shape`, sel?.shape ?? "");
    setValue(`stones.${idx}.size`, sel?.size ?? "");
    setValue(`stones.${idx}.color`, sel?.color ?? "");
    setValue(`stones.${idx}.settingType`, sel?.settingType ?? "");
    setValue(`stones.${idx}.stnPopupWt`, sel?.stnPopupWt ?? "");
    setValue(`stones.${idx}.stnPopupRateByWt`, sel?.stnPopupRateByWt ?? "");
    setValue(`stones.${idx}.stnPopupPcs`, sel?.stnPopupPcs ?? "");
    setValue(`stones.${idx}.stnPopupRateByPcs`, sel?.stnPopupRateByPcs ?? "");
    setValue(`stones.${idx}.stnPopupDescription`, sel?.stnPopupDescription ?? "");
    setValue(`stones.${idx}.stnPopupAmt`, sel?.stnPopupAmount ?? "");
  };

  const recomputeAmt = (i) => {
    const wt = parseFloat(getValues(`stones.${i}.stnPopupWt`));
    const rWt = parseFloat(getValues(`stones.${i}.stnPopupRateByWt`));
    const pcs = parseInt(getValues(`stones.${i}.stnPopupPcs`), 10);
    const rPc = parseFloat(getValues(`stones.${i}.stnPopupRateByPcs`));
    let amt = "";
    if (!isNaN(wt) && !isNaN(rWt) && (isNaN(pcs) || pcs === 0)) amt = (wt * rWt).toFixed(2);
    else if (!isNaN(pcs) && !isNaN(rPc)) amt = (pcs * rPc).toFixed(2);
    setValue(`stones.${i}.stnPopupAmt`, amt, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const requestClose = () => (onClose ? onClose() : setOpenStoneModal?.(false));

  const doSave = (data) => {
    onSave?.(data?.stones || []);
    requestClose();
  };

  // ESC to close
  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        requestClose();
      }
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Backdrop click to close
  const onBackdropMouseDown = (e) => {
    if (panelRef.current && !panelRef.current.contains(e.target)) requestClose();
  };

  const rows = watch("stones") || [];
  const totals = {
    totalWt: rows.reduce((s, x) => s + (parseFloat(x?.stnPopupWt) || 0), 0).toFixed(3),
    totalPcs: rows.reduce((s, x) => s + (parseInt(x?.stnPopupPcs, 10) || 0), 0),
    totalAmt: rows.reduce((s, x) => s + (parseFloat(x?.stnPopupAmt) || 0), 0).toFixed(2),
  };

  const scrollRowIntoView = (idx) => {
    const el = rowRefs.current?.[idx];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        key="backdrop"
        className="fixed inset-0 z-50 bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onMouseDown={onBackdropMouseDown}
      />
      {/* Panel */}
      <motion.div
        key="panel"
        className="fixed inset-0 z-50 grid place-items-center p-4"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.2 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-stone-title"
        onAnimationComplete={() => focusWithRetries()} // one-shot; will disarm itself
      >
        <div
          ref={panelRef}
          className="w-full max-w-5xl max-h-[260px] rounded-xl border border-violet-300 bg-[#f7f4ff] shadow-2xl flex flex-col"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-violet-300 rounded-t-xl px-4 py-2 flex items-center justify-between">
            <h5 id="invoice-stone-title" className="text-sm font-semibold text-violet-800">
              💎 Stones
            </h5>
            <button
              type="button"
              onClick={requestClose}
              className="p-2 text-slate-600 hover:text-slate-800"
              aria-label="Close stones popup"
            >
              <FaTimes />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-3 pt-2 pb-16 snap-y snap-mandatory">
            {fields.map((field, idx) => {
              const unit = watch(`stones.${idx}.unit`);
              const hasWt = !!watch(`stones.${idx}.stnPopupWt`);
              const hasRateWt = !!watch(`stones.${idx}.stnPopupRateByWt`);
              const hasPcs = !!watch(`stones.${idx}.stnPopupPcs`);
              const hasRatePcs = !!watch(`stones.${idx}.stnPopupRateByPcs`);
              const weightMode = hasWt || hasRateWt;
              const pcsMode = hasPcs || hasRatePcs;

              return (
                <div
                  key={field.id}
                  ref={(el) => (rowRefs.current[idx] = el)}
                  className="snap-center border border-violet-200 bg-white rounded-md p-2 shadow-sm mb-2 relative"
                >
                  <div className="absolute -left-2 -top-2 bg-violet-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center shadow">
                    {idx + 1}
                  </div>

                  <div className="grid grid-cols-6 gap-2 mb-2">
                    {/* Name (controlled select) */}
                    <div>
                      <label htmlFor={`stone-name-${idx}`} className="text-[10px] text-gray-600">
                        Name
                      </label>
                      <div className="rounded-md border border-gray-300 focus-within:ring-2 focus-within:ring-violet-400 focus-within:border-violet-400 transition-shadow">
                        <Controller
                          name={`stones.${idx}.stnPopupName`}
                          control={control}
                          render={({ field }) => (
                            <select
                              {...field}
                              id={`stone-name-${idx}`}
                              autoComplete="off"
                              name={`stones.${idx}.stnPopupName`}
                              className="w-full bg-white px-2 py-0.5 text-xs rounded-md focus:ring-2 focus:ring-violet-400 focus:border-violet-400"
                              tabIndex={0}
                              ref={(el) => {
                                // keep first-focus logic
                                if (idx === 0) {
                                  firstSelectRef.current = el;
                                  if (el && focusArmedRef.current) {
                                    setTimeout(() => {
                                      if (focusArmedRef.current) {
                                        el.focus({ preventScroll: true });
                                        focusArmedRef.current = false;
                                      }
                                    }, 0);
                                  }
                                }
                              }}
                              onChange={(e) => {
                                field.onChange(e);
                                onNamePicked(idx, e.target.value);
                              }}
                            >
                              <option value="">--Select--</option>
                              {stonesData?.map((s) => (
                                <option key={s._id} value={s._id}>
                                  {s.stoneName}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                      </div>
                    </div>

                    {/* Wt */}
                    <div>
                      <label className="text-[10px] text-gray-600">
                        Wt {unit === "gram" ? "(gm)" : "(ct)"}
                      </label>
                      <input
                        {...register(`stones.${idx}.stnPopupWt`, {
                          onChange: () => recomputeAmt(idx),
                          validate: (v) => v === "" || DECIMAL_RE.test(String(v)) || "Only numbers",
                        })}
                        placeholder="Wt"
                        inputMode="decimal"
                        onKeyDown={(e) =>
                          handleDecimalKeyDown(e, watch(`stones.${idx}.stnPopupWt`) ?? "")
                        }
                        onPaste={handleDecimalPaste}
                        disabled={pcsMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Rate/Wt */}
                    <div>
                      <label className="text-[10px] text-gray-600">Rate/Wt</label>
                      <input
                        {...register(`stones.${idx}.stnPopupRateByWt`, {
                          onChange: () => recomputeAmt(idx),
                          validate: (v) => v === "" || DECIMAL_RE.test(String(v)) || "Only numbers",
                        })}
                        placeholder="Rate/Wt"
                        inputMode="decimal"
                        onKeyDown={(e) =>
                          handleDecimalKeyDown(e, watch(`stones.${idx}.stnPopupRateByWt`) ?? "")
                        }
                        onPaste={handleDecimalPaste}
                        disabled={pcsMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Pcs */}
                    <div>
                      <label className="text-[10px] text-gray-600">Pcs</label>
                      <input
                        {...register(`stones.${idx}.stnPopupPcs`, {
                          onChange: () => recomputeAmt(idx),
                          setValueAs: (v) => (v === "" ? "" : String(v).replace(/\D/g, "")),
                        })}
                        placeholder="Pcs"
                        onBeforeInput={handleIntegerBeforeInput}
                        onKeyDown={handleIntegerKeyDown}
                        onPaste={handleIntegerPaste}
                        disabled={weightMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Rate/Pcs */}
                    <div>
                      <label className="text-[10px] text-gray-600">Rate/Pcs</label>
                      <input
                        {...register(`stones.${idx}.stnPopupRateByPcs`, {
                          onChange: () => recomputeAmt(idx),
                          validate: (v) => v === "" || DECIMAL_RE.test(String(v)) || "Only numbers",
                        })}
                        placeholder="Rate/Pcs"
                        inputMode="decimal"
                        onKeyDown={(e) =>
                          handleDecimalKeyDown(e, watch(`stones.${idx}.stnPopupRateByPcs`) ?? "")
                        }
                        onPaste={handleDecimalPaste}
                        disabled={weightMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Amt */}
                    <div>
                      <label className="text-[10px] text-gray-600">Amt</label>
                      <input
                        {...register(`stones.${idx}.stnPopupAmt`)}
                        placeholder="0.00"
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>
                  </div>

                  {/* Second line */}
                  <div className="grid grid-cols-4 gap-2 items-end">
                    <div>
                      <label className="text-[10px] text-gray-600">Deduct</label>
                      <select
                        {...register(`stones.${idx}.stnPopupDeduct`)}
                        className="border px-2 py-0.5 rounded text-xs w-full border-gray-300"
                      >
                        <option value="">--Select--</option>
                        <option value="deduct">Deduct</option>
                        <option value="add">Add</option>
                        <option value="nonDeduct">Non-Deduct</option>
                      </select>
                    </div>

                    <div className="col-span-2">
                      <label className="text-[10px] text-gray-600">Description</label>
                      <input
                        {...register(`stones.${idx}.stnPopupDescription`)}
                        placeholder="Description"
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    <div className="flex gap-2 justify-center">
                      <button
                        type="button"
                        onClick={() => {
                          const prev = Math.max(0, idx - 1);
                          remove(idx);
                          setTimeout(() => {
                            setFocus(`stones.${prev}.stnPopupName`);
                            scrollRowIntoView(prev);
                          }, 150);
                        }}
                        className="border px-2 py-1 rounded text-xs border-gray-400 hover:bg-red-50"
                        title="Remove row"
                      >
                        <FaTrash size={10} className="text-gray-500" />
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          const newIndex = fields.length;
                          append(defaultStoneFields);
                          requestAnimationFrame(() => {
                            setTimeout(() => {
                              setFocus(`stones.${newIndex}.stnPopupName`);
                              scrollRowIntoView(newIndex);
                            }, 120);
                          });
                        }}
                        className="border px-2 py-1 rounded text-xs border-gray-400 hover:bg-green-50"
                        title="Add row"
                      >
                        <FaPlus size={10} className="text-gray-500" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Totals chip */}
          <div className="sticky bottom-10 flex justify-center pointer-events-none">
            <div className="bg-violet-100 border border-violet-300 rounded-full text-[11px] px-4 py-1 shadow-md text-violet-700 flex gap-4">
              <span>
                💎 Total Wt: <strong>{totals.totalWt}</strong>
              </span>
              <span>
                🔹 Total Pcs: <strong>{totals.totalPcs}</strong>
              </span>
              <span>
                💰 Total Amt: <strong>{totals.totalAmt}</strong>
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-[#f7f4ff] border-t border-violet-300 px-3 py-2 flex justify-end gap-2 rounded-b-xl">
            <button
              type="button"
              onClick={requestClose}
              className="text-xs px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit(doSave)}
              className="text-xs px-3 py-1 bg-violet-600 text-white rounded hover:bg-violet-700"
            >
              Save
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InvoiceStonePopUp;
